import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { ROUTE_PATHS } from '../../routes/routePaths';
import { useGetCategoryHomePage } from '../../services/productServices';
import Breadcrumb from './Breadcrumb';
import { UserNavbar } from './UserNavbar';

type HeaderProps = {
  readonly onToggleSidebar: () => void;
  readonly isContentTileView?: boolean;
};

// Determine environment based on current URL
function getHeaderBannerText(): string | null {
  const currentUrl = window.location.hostname.toLowerCase();

  if (currentUrl.includes('localhost') || currentUrl.includes('aosphere-local')) {
    return 'localhost';
  }

  if (currentUrl.includes('dev1.')) {
    return 'NextGen-DEV';
  }

  if (currentUrl.includes('stage1.')) {
    return 'UAT';
  }

  if (currentUrl === 'demo.aoslogin.net') {
    return 'Demo-UAT';
  }

  // Otherwise, it's prod - show nothing
  return null;
}

export default function Header({ onToggleSidebar, isContentTileView }: HeaderProps) {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { refetch: refetchCategoryHomePage } = useGetCategoryHomePage();

  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const environmentText = getHeaderBannerText();

  // Repeat environment text 5 times (like static information)
  const repeatedEnvironmentText = environmentText
    ? Array.from({length: 12}).fill(environmentText).join(' ')
    : '';

  // eslint-disable-next-line unicorn/consistent-function-scoping
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  const handleLogoClick = async (error: React.MouseEvent) => {
    if (location.pathname === ROUTE_PATHS?.CATEGORY_HOME) {
      error.preventDefault();
      try {
        await refetchCategoryHomePage();
        scrollToTop();
      } catch {
        // 401 / session expired: axios interceptor redirects to login?error=sessionExpired
      }
    } else {
      error.preventDefault();
      navigate(ROUTE_PATHS.CATEGORY_HOME);
    }
  };

  return (
    <div
      className={`ao_header ${environmentText ? 'staticInfo' : ''}`}
      style={{
        '--after-content': repeatedEnvironmentText ? `' ${repeatedEnvironmentText} '` : '',
      } as React.CSSProperties}
    >
      <nav className={`navbar fixed-top${isScrolled ? ' shadow' : ''}`}>
        <div
          className="ao_mainContainer d-flex align-items-center"
          style={isContentTileView ? { maxWidth: 'none' } : undefined}
        >
          <div className="ao_hamburger">
            <button onClick={onToggleSidebar} className="navbar-toggler">
              <svg
                width="24"
                height="18"
                viewBox="0 0 24 18"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M22.4999 18H1.50001C0.671547 18 0 17.3284 0 16.5C0 15.6716 0.671547 15 1.50001 15H22.5C23.3284 15 24 15.6716 24 16.5C23.9999 17.3284 23.3284 18 22.4999 18Z"
                  fill="#475C6D"
                />
                <path
                  d="M22.4999 10.5H1.50001C0.671547 10.5 0 9.82848 0 9.00007C0 8.17166 0.671547 7.50006 1.50001 7.50006H22.5C23.3284 7.50006 24 8.17161 24 9.00007C24 9.82853 23.3284 10.5 22.4999 10.5Z"
                  fill="#475C6D"
                />
                <path
                  d="M22.4999 3.00001H1.50001C0.671547 3.00001 0 2.32847 0 1.50001C0 0.671547 0.671547 0 1.50001 0H22.5C23.3284 0 24 0.671547 24 1.50001C24 2.32847 23.3284 3.00001 22.4999 3.00001Z"
                  fill="#475C6D"
                />
              </svg>
            </button>
          </div>

          <div className="ao_logo ms-3">
            <Link to={ROUTE_PATHS?.CATEGORY_HOME} onClick={handleLogoClick}>
              <img
                src="/assets/images/newdesign/aosphere_an_affiliate_logo.svg?dt=08_02_2024"
                height="35"
                alt="AOSphere"
                title="AOSphere"
              />
            </Link>
          </div>
          <div className="ms-auto d-flex align-items-center">
            <Breadcrumb />
            <UserNavbar user={{ userEmail: user?.emailAddress ?? '' }} />
          </div>
          {/* Additional JSX content continues here */}
        </div>
      </nav>
    </div>
  );
}
